import { LucideIcon } from 'lucide-react';
import React from 'react';
import styled from 'styled-components';
import { BodyCell } from '../../../components_v2/table/style/Style';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { DefaultText } from '../../../styles/global/css/GlobalText';
import { BorderColor, PrimaryColor, RedSidely, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';

const Container = styled.div`
	height: 100%;
	width: 100%;
	margin-right: -10px;
	display: flex;
  	flex-flow: column;
    overflow: hidden;
`;

export const PARTIAL_OPEN_ICON_SIZE = 12;

const PartialOpen = styled.div <{ parent?}>`
	border: 1px solid ${PrimaryColor};
	border-radius: 5px;
    transition: opacity 0.4s ease;
    opacity: 0;
    cursor: pointer;
    padding: 2px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;

    ${p => p.parent ?? BodyCell}:hover & {
        opacity: 1;
    }

    &:hover {
        opacity: 1;
    }
`;

export function TableCellButton(props: {
	icon: LucideIcon,
	onClick?: () => void,
	parent?
}): React.JSX.Element {
	return <PartialOpen parent={props.parent} onClick={props.onClick}>
		<props.icon size={PARTIAL_OPEN_ICON_SIZE} color={PrimaryColor} />
	</PartialOpen>;
}

interface TagProps {
	backgroundColor: string
}

const Tag = styled.p<TagProps>`
    ${DefaultText}
    background-color: ${SurfaceColor};
    border-radius: 5px;
    line-height: 18px;
    color: ${p => p.backgroundColor};
    font-size: 12px;
    padding: 3px 12px;
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
`;

interface TagContainerProps {
	backgroundColor?: string | null
}

const TagContainer = styled.div<TagContainerProps>`
    border: 1px solid ${p => p.backgroundColor ?? SidelyBlack};
    display: flex;
    margin: 5px 2px;
    border-radius: 5px;
`;

const TagDelete = styled.div<TagContainerProps>`
    ${DefaultText}
    background-color: ${SurfaceColor};
    transition: all 0.4s ease;
    padding: 2px 5px;
    color: ${p => p.backgroundColor ?? SidelyBlack};
    border-left: 1px solid ${p => p.backgroundColor ?? SidelyBlack};
    font-size: 15px;
    font-weight: 300;
    line-height: 18px;
    border-radius: 0 4px 4px 0;

    &:before {
        content: 'x';
    }

    &:hover {
        color: white;
        background-color: ${p => p.backgroundColor ?? SidelyBlack};
    }
`;

export const SelectAllDiv = styled.div`
    ${DefaultText}
    display: flex;
    align-items: center;
    height: 100%;
    font-size: 12px;
    color: ${SidelyBlack};
    font-weight: 600;
    gap: 24px;
    margin-left: 8px;
`;

export const SelectionActionContainer = styled.div<{ height?: string }>`
    height: ${p => p.height ?? '100%'};
    border-left: solid 1px ${BorderColor};
    display: flex;
    align-items: center;
    padding: 0 10px;
    gap: 5px;
`;

export const SelectionAction = styled.div<{
	delete?: boolean,
	titleHover?: string,
	hoverSvgColor?: string,
	hoverBackgroundColor?: string,
	color?: string,
	gap?: string,
	borderRadius?: string,
	padding?: string
}>`
    display: flex;
    align-items: center;
    cursor: pointer;
    color: ${p => p.color ?? (p.delete ? RedSidely : SidelyBlack)};
    font-weight: 500;
    font-size: 12px;
    white-space:nowrap;
    gap: ${p => p.gap ?? '2px'};
    position: relative;
    padding: ${p => p.padding ?? '4px'};
    border-radius: ${p => p.borderRadius ?? '5px'};
    &:hover {
        background-color: ${p => p.hoverBackgroundColor ?? `color-mix(in oklab, ${SidelyBlack} 10%, transparent)`};
        ${p => p.hoverSvgColor ? `& > svg {
			color: ${p.hoverSvgColor};
		}` : ''}
    }
${(p) => {
		if (p.titleHover) {
			return `
            width:100%;
            &:hover {
                ::before {
                    content: '${p.titleHover}';
                    position: absolute;
                    transform: translate(-50%, calc(100% + 5px));
                    left: 50%;
                    background-color: ${SurfaceColor};
                    color: ${p.delete ? RedSidely : SidelyBlack};
                    font-weight: 400;
                    font-size: 12px;
                    padding: 5px;
                    border: 1px solid ${BorderColor};
                    border-radius: 5px;
                    z-index: 100;
                }
            }
        `;
		}
		return '';
	}
}
`;

export const NoDataText = styled.p`
    ${DefaultText}
    padding-top: 15px;
    padding-left: 35px;
`;

export const SidebarButton = styled(DefaultButton)`
    margin: 0;
    white-space:nowrap;
    font-weight: 600;
`;

export {
	Container,
	PartialOpen,
	Tag,
	TagContainer,
	TagDelete
};
